import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, Search, ChevronLeft, ChevronRight } from "lucide-react";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  update: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  delete: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  submit: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({ action: "", entity_type: "" });
  const LIMIT = 50;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(LIMIT) });
      if (filters.action) params.set("action", filters.action);
      if (filters.entity_type) params.set("entity_type", filters.entity_type);
      const r = await api.get(`/audit-log?${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [skip, filters]);

  return (
    <div className="space-y-6 ih-anim-in" data-testid="audit-log-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5"><Activity className="h-3 w-3" /> Configurazione</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Registro <span className="font-medium">attività</span></h1>
        <p className="text-sm text-muted-foreground mt-1">Tracciamento delle operazioni · {total} record totali</p>
      </div>

      <div className="ih-glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <select data-testid="al-action" value={filters.action} onChange={(e) => { setSkip(0); setFilters({ ...filters, action: e.target.value }); }} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutte le azioni</option>
          <option value="create">create</option>
          <option value="update">update</option>
          <option value="delete">delete</option>
          <option value="submit">submit</option>
          <option value="login">login</option>
          <option value="evaluate">evaluate</option>
        </select>
        <select data-testid="al-entity" value={filters.entity_type} onChange={(e) => { setSkip(0); setFilters({ ...filters, entity_type: e.target.value }); }} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutte le entità</option>
          <option value="user">user</option>
          <option value="hotel">hotel</option>
          <option value="audit">audit</option>
          <option value="resident_report">resident_report</option>
          <option value="alert_rule">alert_rule</option>
          <option value="staff">staff</option>
          <option value="hk_productivity">hk_productivity</option>
        </select>
      </div>

      <div className="ih-glass rounded-2xl p-6">
        {loading ? "Caricamento…" : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun record nel filtro.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="al-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border/60">
                  <th className="py-2 pr-3">Data/Ora</th>
                  <th className="py-2 pr-3">Utente</th>
                  <th className="py-2 pr-3">Azione</th>
                  <th className="py-2 pr-3">Entità</th>
                  <th className="py-2 pr-3">Entity ID</th>
                  <th className="py-2 pr-3">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-border/40">
                    <td className="py-2 pr-3 text-muted-foreground text-xs">{new Date(it.timestamp).toLocaleString("it-IT")}</td>
                    <td className="py-2 pr-3 text-xs">{it.user ? `${it.user.nome} ${it.user.cognome}` : it.user_id?.slice(0, 8)}</td>
                    <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ACTION_COLORS[it.action] || "bg-slate-100 text-slate-700"}`}>{it.action}</span></td>
                    <td className="py-2 pr-3 text-xs">{it.entity_type}</td>
                    <td className="py-2 pr-3 text-[10px] font-mono text-muted-foreground">{it.entity_id?.slice(0, 12)}…</td>
                    <td className="py-2 pr-3 text-[10px] text-muted-foreground max-w-[280px] truncate" title={JSON.stringify(it.details)}>{JSON.stringify(it.details).slice(0, 80)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 text-xs">
          <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + LIMIT, total)} di {total}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <button disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
